import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { tenantSettingsApi } from '../api/tenantSettingsApi';
import { tenantQueryKeys } from './useTenantQuery';
import type { ClioFormData } from '@xtnd/web/shared/types/tenantSettingTypes';

/**
 * Hook to save Clio configuration
 */
export function useClioConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ClioFormData) => {
      // Get current tenant config to preserve existing modules
      const currentConfig = await tenantSettingsApi.getCurrentTenantConfig();
      const webhookEndpoint = import.meta.env.VITE_API_URL || 'https://api.d.xtnd.legal';

      // Build clioAPI module update
      const clioAPIUpdate: any = {
        clientId: data.clientId,
        baseUrl: 'https://app.goclio.com',
        redirectUri: `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/auth/clio/callback`,
        clioWebHookUri: webhookEndpoint,
        webhooksConfigured: data.enableWebhooks,
        webhookSubscriptions: data.webhookSubscriptions,
        healthCheckEndpoint: '/health/clioAPI',
        settingsComponent: 'ClioConfigModal',
        webhookUrl: `${webhookEndpoint}/webhook/clio/v1/0000000001`,
      };

      // Preserve existing SSM paths if no new values provided
      const existingClioModule = currentConfig.modules?.clioAPI;

      if (data.appId && data.appId.trim()) {
        clioAPIUpdate.appId = data.appId;
      } else if (existingClioModule?.appId) {
        clioAPIUpdate.appId = existingClioModule.appId;
      }

      if (data.clientSecret && data.clientSecret.trim()) {
        clioAPIUpdate.clientSecret = data.clientSecret;
      } else if (existingClioModule?.clientSecret) {
        clioAPIUpdate.clientSecret = existingClioModule.clientSecret;
      }

      if (data.authorizationCode && data.authorizationCode.trim()) {
        clioAPIUpdate.authorizationCode = data.authorizationCode;
      } else if (existingClioModule?.authorizationCode) {
        clioAPIUpdate.authorizationCode = existingClioModule.authorizationCode;
      }

      if (data.accessToken && data.accessToken.trim()) {
        clioAPIUpdate.accessToken = data.accessToken;
      } else if (existingClioModule?.accessToken) {
        clioAPIUpdate.accessToken = existingClioModule.accessToken;
      }

      const { clioAPI: _, ...otherModules } = currentConfig.modules || {};

      const apiPayload = {
        modules: {
          ...otherModules,
          clioAPI: clioAPIUpdate,
        },
      };

      return await tenantSettingsApi.updateCurrentTenantConfig(apiPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.config });
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.configurationStatus });
      showToast.success('Clio configuration saved successfully!');
    },
    onError: error => {
      console.error('Clio config save error:', error);
      showToast.error('Failed to save Clio configuration');
    },
  });
}

